import statusService from '../../services/tickets/status.service.js'

import entityUtility from '../../utils/entity.utility.js'
import httpUtility from '../../utils/http.utility.js'
import sortUtility from '../../utils/sort.utility.js'

import { useToast } from '../../composables/useToast.js'

import { t } from '@/i18n'

const toast = useToast()

async function saveStatus(scope, payload) {
  try {
    let response
    if (payload._id) response = await statusService.update.status(scope, payload._id, payload)
    else response = await statusService.create.status(scope, payload)
    return response.data.item
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.settings.errors.saveStatus')))
    return null
  }
}

async function deleteStatus(scope, id) {
  try {
    await statusService.delete.status(scope, id)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.settings.errors.deleteItem')))
    return false
  }
}

function sortStatuses(array) {
  return sortUtility.ascending.sort(array)
}

function mapStatuses(array) {
  return array.map((status) => ({
    value: status._id,
    label: status.title,
    color: status.color,
  }))
}

function findStatus(id, array) {
  return entityUtility.find(id, array)
}

class StatusController {
  /**
   * Return a copy of `array` sorted ascending by `order`. Does not
   * mutate `array`.
   *
   * @template {{ order?: number|null, _id: string }} T
   * @param {T[]} array
   * @returns {T[]}
   */
  sort(array) {
    return sortStatuses(array)
  }

  /**
   * Map each status to `{ value, label, color }`.
   *
   * @param {object[]} array
   * @returns {Array<{ value: string, label: string, color?: string }>}
   */
  map(array) {
    return mapStatuses(array)
  }

  /**
   * Look up one status by `id` in `array`. Returns the row, or `null`
   * when `id` is missing or not found.
   *
   * @param {string|null|undefined} id
   * @param {Array<{ _id: string }>} array
   * @returns {object|null}
   */
  find(id, array) {
    return findStatus(id, array)
  }

  save = {
    /**
     * Create or update a status row. Branches on `payload._id`
     * (truthy → update; omitted → create). Returns the saved status on
     * success, `null` on failure (toasts the error).
     *
     * @param {string} scope - Settings API URL prefix (`ticket-account` or `ticket-group/:id`).
     * @param {object} payload
     * @returns {Promise<object|null>}
     */
    async status(scope, payload) {
      return saveStatus(scope, payload)
    },
  }

  delete = {
    /**
     * Delete a status row. Returns `true` on success, `false` on
     * failure (toasts the error).
     *
     * @param {string} scope - Settings API URL prefix (`ticket-account` or `ticket-group/:id`).
     * @param {string} id
     * @returns {Promise<boolean>}
     */
    async status(scope, id) {
      return deleteStatus(scope, id)
    },
  }
}

export default new StatusController()
